import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { ActiveProblemPrmFindControlDto, AssessmentsUpdatedDto, Bridge, GenericEventSink, PredefinedUserSettingValue } from 'webcommon/shared';

export interface GlobalBridgeEventTypes {
  // patient-dashboard
  'assessmentUpdated': AssessmentsUpdatedDto;
  'closePatientDashboard': { Message: string | null };
  'getPredefinedUserSetting': { name: string };
  'openAssessments': { visitId: string; visitAssessmentFormId: string };
  'openAttachment': { attachmentId: string };
  'openFindProblem': { InstanceId: number | null };
  'predefinedUserSettingListener': PredefinedUserSettingValue;
  'problems-selection-from-prm': ActiveProblemPrmFindControlDto | null;
  'registerLock': { InstanceId: string };
  'review-related-visits': { Pk: string };
  'display-info-web-page': { Pk: string };
  'updateBatch': { ARBatchId: string };
  'userSettingListener': string;
  'userSettingRequest': { prmPath: string; attribute: string };
  'userSettingSet': { prmPath: string; attribute: string; value: string };
  'userSettingSetBool': boolean;
  'visitIdChangedFND': { InstanceId: string };
}


// todo: restrict this import with eslint once usages are replaced with GenericEventManager
// todo: delete, or privatize addEventListener, and removeEventListener, so they don't accidentally get used
@Injectable({
  providedIn: 'root',
})
export class BridgeService implements Bridge, GenericEventSink {
  private bridge: Bridge | null = null;

  public loadConfiguration(configuration: Bridge): void {
    if (this.bridge !== null) {
      throw new Error('bridge has already been set');
    }
    this.bridge = configuration;
  }

  private validateConfiguration(): Bridge {
    if (this.bridge === null) {
      throw new Error('bridge has not been set');
    }
    return this.bridge;
  }

  public hasConfiguration(): boolean {
    return this.bridge !== null;
  }

  public fireEvent(eventName: string, data: any = null): void {
    const bridge = this.validateConfiguration();
    bridge.fireEvent(eventName, data === null ? {} : data);
  }

  addEventListener(eventName: string, callbackFn: (data: any) => void): void {
    const bridge = this.validateConfiguration();
    bridge.addEventListener(eventName, callbackFn);
  }

  removeEventListener(eventName: string, callbackFn: (data: any) => void): void {
    const bridge = this.validateConfiguration();
    bridge.removeEventListener(eventName, callbackFn);
  }

  on<T>(eventName: string): Observable<T> {
    const bridge = this.validateConfiguration();
    return bridge.on<T>(eventName);
  }

  once<T>(eventName: string): Observable<T> {
    const bridge = this.validateConfiguration();
    return bridge.once<T>(eventName);
  }
}
